import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { t, translateError } from '../i18n.js';
import { api } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import AiAssist, { useAiAvailable } from '../components/shared/AiAssist.jsx';
import { aiError } from '../utils/ai.js';

// ── helpers ───────────────────────────────────────────────────────────────────

// ✨ A proposal of steps (with tasks) from the description of a new template. They are
// shown first and only added when the member keeps them and creates the template.
function StepsFromDescription({ description, name, steps, onSteps }) {
  const available = useAiAvailable();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!available || (!steps && !description.trim())) return null;
  const ask = async () => {
    setBusy(true); setError(null);
    try {
      const { steps: proposed } = await api('/api/ai-help/structure', { method: 'POST', body: JSON.stringify({ kind: 'template_steps', text: description, context: { title: name } }) });
      if (!proposed.length) setError('De AI vond geen stappen in deze beschrijving. Beschrijf het proces wat uitgebreider.');
      onSteps(proposed.length ? proposed : null);
    } catch (err) { setError(aiError(err)); }
    setBusy(false);
  };
  const btn = 'border:1px solid var(--color-border);background:var(--color-bg-secondary);border-radius:6px;padding:3px 9px;font-size:12px;cursor:pointer;color:var(--color-text)';
  return (
    <div style="margin-top:6px">
      {!steps && <button type="button" style={btn} onClick={ask} disabled={busy}>{busy ? '✨ Bezig…' : '✨ Stappen voorstellen uit de beschrijving'}</button>}
      {error && <div class="text-sm" style="color:var(--color-danger,#b91c1c);margin-top:4px">{error}</div>}
      {steps && (
        <div style="border:1px dashed var(--color-border);border-radius:8px;padding:10px;margin-top:4px;background:var(--color-bg-secondary)">
          <div class="text-sm text-muted" style="margin-bottom:6px">Voorstel van de AI — deze stappen worden toegevoegd als je het sjabloon aanmaakt. Daarna kun je ze gewoon aanpassen.</div>
          <ol style="margin:0;padding-left:20px;list-style:decimal;font-size:13px;display:flex;flex-direction:column;gap:4px">
            {steps.map((st, i) => (
              <li key={i}>
                <strong>{st.title}</strong>{st.description && <span class="text-muted"> — {st.description}</span>}
                {st.tasks.length > 0 && <div class="text-muted" style="font-size:12px">Taken: {st.tasks.map(t => t.title).join(' · ')}</div>}
              </li>
            ))}
          </ol>
          <div style="display:flex;gap:6px;margin-top:8px">
            <button type="button" style={btn} onClick={ask} disabled={busy}>{busy ? 'Bezig…' : 'Opnieuw'}</button>
            <button type="button" style={btn} onClick={() => onSteps(null)}>Niet meenemen</button>
          </div>
        </div>
      )}
    </div>
  );
}

function useModal() {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const close = () => { setOpen(false); setError(null); };
  return { open, setOpen, error, setError, close };
}

function useConfirm() {
  const [state, setState] = useState({ open: false, title: '', message: '', onConfirm: null, loading: false });
  const ask = (title, message, onConfirm) => setState({ open: true, title, message, onConfirm, loading: false });
  const close = () => setState((s) => ({ ...s, open: false, loading: false }));
  const confirm = async () => {
    setState((s) => ({ ...s, loading: true }));
    try { await state.onConfirm(); } finally { close(); }
  };
  return { state, ask, close, confirm };
}

function TemplatesSkeleton() {
  return (
    <div>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} style="display:grid;grid-template-columns:1fr 160px 80px 90px;gap:12px;padding:12px 16px;border-bottom:1px solid var(--color-border);align-items:center">
          <div>
            <div class="skeleton" style="height:14px;width:55%;border-radius:4px;margin-bottom:5px" />
            <div class="skeleton" style="height:11px;width:35%;border-radius:4px" />
          </div>
          <div class="skeleton" style="height:12px;width:80%;border-radius:4px" />
          <div class="skeleton" style="height:12px;width:50px;border-radius:4px" />
          <div class="skeleton" style="height:22px;width:70px;border-radius:12px" />
        </div>
      ))}
    </div>
  );
}

// ── StepModal (add + edit) ────────────────────────────────────────────────────

function StepModal({ templateId, stepToEdit, onSaved, modal, roles, members }) {
  const isEdit = !!stepToEdit;

  const blankForm = { title: '', description: '', step_order: '', is_approval_step: false, responsible_role_id: '', step_type: 'normal', condition_question: '', condition_false_jump_to: '', parallel_group: '', sla_hours: '' };
  const blankTask = { title: '', description: '', responsible_role_id: '', owner_user_id: '' };

  const [form, setForm] = useState(blankForm);
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState(blankTask);
  const [usersForTaskRole, setUsersForTaskRole] = useState([]);
  const [saving, setSaving] = useState(false);

  // Sync state when stepToEdit changes (switching between add/edit)
  useEffect(() => {
    if (stepToEdit) {
      setForm({
        title: stepToEdit.title || '',
        description: stepToEdit.description || '',
        step_order: stepToEdit.step_order || '',
        is_approval_step: !!stepToEdit.is_approval_step,
        responsible_role_id: stepToEdit.responsible_role_id || '',
        step_type: stepToEdit.step_type || 'normal',
        condition_question: stepToEdit.condition_question || '',
        condition_false_jump_to: stepToEdit.condition_false_jump_to || '',
        parallel_group: stepToEdit.parallel_group || '',
        sla_hours: stepToEdit.sla_hours || '',
      });
      try {
        setTasks(stepToEdit.default_tasks_json ? JSON.parse(stepToEdit.default_tasks_json) : []);
      } catch {
        setTasks([]);
      }
    } else {
      setForm(blankForm);
      setTasks([]);
    }
    setNewTask(blankTask);
    setUsersForTaskRole([]);
  }, [stepToEdit, modal.open]);

  // When task role changes, fetch filtered user list
  useEffect(() => {
    if (newTask.responsible_role_id) {
      api(`/api/users/members/by-role/${newTask.responsible_role_id}`)
        .then(setUsersForTaskRole)
        .catch(() => setUsersForTaskRole([]));
    } else {
      setUsersForTaskRole([]);
    }
  }, [newTask.responsible_role_id]);

  const addTask = () => {
    if (!newTask.title.trim()) return;
    setTasks((prev) => [...prev, {
      title: newTask.title.trim(),
      description: newTask.description.trim(),
      responsible_role_id: newTask.responsible_role_id || null,
      default_owner_id: newTask.owner_user_id ? Number(newTask.owner_user_id) : null,
    }]);
    setNewTask(blankTask);
    setUsersForTaskRole([]);
  };

  const removeTask = (i) => setTasks((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    setSaving(true);
    modal.setError(null);
    try {
      const payload = {
        ...form,
        step_order: form.step_order !== '' ? Number(form.step_order) : undefined,
        default_tasks_json: tasks.length ? JSON.stringify(tasks) : null,
        responsible_role_id: form.responsible_role_id ? Number(form.responsible_role_id) : null,
        condition_false_jump_to: form.condition_false_jump_to !== '' ? Number(form.condition_false_jump_to) : null,
        sla_hours: form.sla_hours !== '' ? Number(form.sla_hours) : null,
        parallel_group: form.parallel_group.trim() || null,
        condition_question: form.condition_question.trim() || null,
      };
      if (isEdit) {
        await api(`/api/templates/${templateId}/steps/${stepToEdit.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await api(`/api/templates/${templateId}/steps`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      modal.close();
      onSaved();
    } catch (err) {
      modal.setError(translateError(err.code, err.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={modal.open}
      title={isEdit ? t('template.editStep') : t('template.addStep')}
      onClose={modal.close}
    >
      <div class="form-group">
        <label class="form-label">{t('template.stepTitle')} *</label>
        <input
          class="form-input"
          type="text"
          value={form.title}
          onInput={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
        />
      </div>
      <div class="form-group">
        <label class="form-label">{t('template.stepDescription')}</label>
        <textarea
          class="form-textarea"
          rows={2}
          value={form.description}
          onInput={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
        />
        <AiAssist kind="template_step_description" value={form.description} context={{ title: form.title }} onUse={text => setForm(p => ({ ...p, description: text }))} />
      </div>
      <div class="form-group">
        <label class="form-label">{t('template.stepOrder')}</label>
        <input
          class="form-input"
          type="number"
          min="1"
          value={form.step_order}
          onInput={(e) => setForm((p) => ({ ...p, step_order: e.target.value }))}
        />
      </div>
      <div class="form-group">
        <label class="form-label">{t('template.responsibleRole')}</label>
        <select
          class="form-select"
          value={form.responsible_role_id}
          onChange={(e) => setForm((p) => ({ ...p, responsible_role_id: e.target.value }))}
        >
          <option value="">{t('template.noRoleRequired')}</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
        <p style="font-size:12px;color:var(--color-text-muted);margin-top:4px">
          {t('template.responsibleRoleHelp')}
        </p>
      </div>

      <div class="form-group">
        <label class="checkbox-label" style="display:flex;align-items:center;gap:8px;cursor:pointer">
          <input
            type="checkbox"
            checked={form.is_approval_step}
            onChange={(e) => setForm((p) => ({ ...p, is_approval_step: e.target.checked }))}
          />
          {t('template.isApprovalStep')}
        </label>
      </div>

      {/* ── Workflow engine ── */}
      <div style="border:1px solid var(--color-border);border-radius:10px;padding:14px;background:var(--color-bg-secondary)">
        <div style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted);margin-bottom:12px">⚙️ Workflow instellingen</div>

        {/* Step type */}
        <div class="form-group" style="margin-bottom:10px">
          <label class="form-label" style="font-size:12px">Staptype</label>
          <div style="display:flex;gap:6px;flex-wrap:wrap">
            {[
              { val: 'normal',    label: '▶ Normaal',   desc: 'Sequentieel' },
              { val: 'condition', label: '◇ Conditie',  desc: 'Ja/Nee vertakking' },
              { val: 'parallel',  label: '⇉ Parallel',  desc: 'Gelijktijdig' },
            ].map(opt => (
              <label key={opt.val} style={`display:flex;flex-direction:column;align-items:center;gap:2px;padding:8px 14px;border-radius:8px;border:2px solid ${form.step_type === opt.val ? 'var(--color-primary)' : 'var(--color-border)'};background:${form.step_type === opt.val ? 'var(--color-primary-light,#eff6ff)' : 'var(--color-surface)'};cursor:pointer;transition:all .15s`}>
                <input
                  type="radio"
                  name="step_type"
                  value={opt.val}
                  checked={form.step_type === opt.val}
                  onChange={() => setForm(p => ({ ...p, step_type: opt.val }))}
                  style="display:none"
                />
                <span style="font-size:13px;font-weight:600">{opt.label}</span>
                <span style="font-size:10px;color:var(--color-text-muted)">{opt.desc}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Condition fields */}
        {form.step_type === 'condition' && (
          <div style="display:flex;flex-direction:column;gap:8px;padding:10px;background:var(--color-surface);border:1px solid #fde68a;border-radius:8px;margin-bottom:10px">
            <div style="font-size:11px;color:#92400e;font-weight:600">◇ Conditie-instellingen</div>
            <div>
              <label class="form-label" style="font-size:12px">Beslissingsvraag *</label>
              <input
                class="form-input"
                type="text"
                placeholder="Bijv. Is de inspectie geslaagd?"
                value={form.condition_question}
                onInput={e => setForm(p => ({ ...p, condition_question: e.target.value }))}
              />
              <p style="font-size:11px;color:var(--color-text-muted);margin-top:3px">Deze vraag verschijnt bij het voltooien van de stap. De uitvoerder kiest Ja of Nee.</p>
            </div>
            <div>
              <label class="form-label" style="font-size:12px">Bij "Nee" — spring naar stap nr.</label>
              <input
                class="form-input"
                type="number"
                min="1"
                placeholder="Stap-nr. (bijv. 5)"
                value={form.condition_false_jump_to}
                onInput={e => setForm(p => ({ ...p, condition_false_jump_to: e.target.value }))}
                style="max-width:160px"
              />
              <p style="font-size:11px;color:var(--color-text-muted);margin-top:3px">Stappen daartussen worden overgeslagen. Bij "Ja" gaat het proces gewoon door.</p>
            </div>
          </div>
        )}

        {/* Parallel group */}
        {form.step_type === 'parallel' && (
          <div style="padding:10px;background:var(--color-surface);border:1px solid #bfdbfe;border-radius:8px;margin-bottom:10px">
            <div style="font-size:11px;color:#1d4ed8;font-weight:600;margin-bottom:6px">⇉ Parallel-groep</div>
            <label class="form-label" style="font-size:12px">Groepsnaam</label>
            <input
              class="form-input"
              type="text"
              placeholder="Bijv. goedkeuringen"
              value={form.parallel_group}
              onInput={e => setForm(p => ({ ...p, parallel_group: e.target.value }))}
            />
            <p style="font-size:11px;color:var(--color-text-muted);margin-top:3px">Stappen met dezelfde groepsnaam worden tegelijk geactiveerd. Het proces gaat pas verder als alle stappen in de groep zijn voltooid.</p>
          </div>
        )}

        {/* SLA hours — available for all step types */}
        <div style="display:flex;align-items:center;gap:10px">
          <div style="flex:1">
            <label class="form-label" style="font-size:12px">⏱ SLA-deadline (uren, optioneel)</label>
            <input
              class="form-input"
              type="number"
              min="1"
              placeholder="Bijv. 48"
              value={form.sla_hours}
              onInput={e => setForm(p => ({ ...p, sla_hours: e.target.value }))}
              style="max-width:160px"
            />
          </div>
          {form.sla_hours && (
            <div style="font-size:12px;color:var(--color-text-muted);padding-top:18px">
              → Escalatiemelding na {form.sla_hours} uur
            </div>
          )}
        </div>
      </div>

      {/* Default tasks */}
      <div class="form-group">
        <label class="form-label" style="margin-bottom:4px">{t('template.defaultTasks')}</label>
        {form.responsible_role_id ? (
          <p style="font-size:12px;color:var(--color-primary);margin:0 0 8px;display:flex;align-items:center;gap:5px">
            <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/></svg>
            {'Worden automatisch toegewezen aan de eerste gebruiker met de rol '}
            <strong>{roles.find(r => String(r.id) === String(form.responsible_role_id))?.name}</strong>
          </p>
        ) : (
          <p style="font-size:12px;color:var(--color-text-muted);margin:0 0 8px">
            Kies eerst een verantwoordelijke rol om taken automatisch toe te wijzen.
          </p>
        )}

        {tasks.length > 0 && (
          <div style="margin-bottom:8px;border:1px solid var(--color-border);border-radius:6px;overflow:hidden">
            {tasks.map((task, i) => {
              const taskRole = task.responsible_role_id
                ? roles.find(r => String(r.id) === String(task.responsible_role_id))
                : null;
              return (
                <div
                  key={i}
                  style="display:flex;align-items:center;gap:10px;padding:8px 12px;border-bottom:1px solid var(--color-border);background:var(--color-bg-secondary)"
                >
                  <div style="flex:1">
                    <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap">
                      <span style="font-size:13px;font-weight:500">{task.title}</span>
                      {taskRole && (
                        <span style="font-size:11px;background:var(--color-primary);color:#fff;padding:1px 7px;border-radius:10px;white-space:nowrap">
                          {taskRole.name}
                        </span>
                      )}
                    </div>
                    {task.description && (
                      <div style="font-size:12px;color:var(--color-text-muted)">{task.description}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeTask(i)}
                    style="background:none;border:none;cursor:pointer;color:var(--color-danger,#dc2626);font-size:16px;padding:0 4px;line-height:1"
                    title={t('common.delete')}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div style="display:flex;gap:6px;align-items:flex-start">
          <div style="flex:1">
            <input
              class="form-input"
              type="text"
              placeholder={t('template.taskTitle')}
              value={newTask.title}
              onInput={(e) => setNewTask((p) => ({ ...p, title: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && addTask()}
              style="margin-bottom:4px"
            />
            <input
              class="form-input"
              type="text"
              placeholder={t('template.taskDescription')}
              value={newTask.description}
              onInput={(e) => setNewTask((p) => ({ ...p, description: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && addTask()}
              style="margin-bottom:4px"
            />
            <select
              class="form-select"
              value={newTask.responsible_role_id}
              onChange={(e) => setNewTask((p) => ({ ...p, responsible_role_id: e.target.value, owner_user_id: '' }))}
              style="margin-bottom:4px"
            >
              <option value="">— Taakeigenaar rol (optioneel)</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            {newTask.responsible_role_id && (
              <select
                class="form-select"
                value={newTask.owner_user_id}
                onChange={(e) => setNewTask((p) => ({ ...p, owner_user_id: e.target.value }))}
              >
                <option value="">— Kies specifieke gebruiker (optioneel)</option>
                {usersForTaskRole.length === 0 ? (
                  <option disabled value="">Geen gebruikers met deze rol gevonden</option>
                ) : (
                  usersForTaskRole.map((u) => (
                    <option key={u.id} value={u.id}>{u.display_name || u.email}</option>
                  ))
                )}
              </select>
            )}
          </div>
          <Button variant="secondary" size="sm" onClick={addTask} disabled={!newTask.title.trim()} style="margin-top:2px">
            {t('common.add')}
          </Button>
        </div>
      </div>

      {modal.error && <div class="alert alert-error mb-3">{modal.error}</div>}
      <div class="flex gap-2 justify-end mt-4">
        <Button variant="secondary" onClick={modal.close}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!form.title || saving}>
          {saving ? t('common.loading') : t('common.save')}
        </Button>
      </div>
    </Modal>
  );
}

// ── VariantModal ──────────────────────────────────────────────────────────────

function VariantModal({ templateId, onSaved, modal }) {
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!modal.open) setForm({ name: '', description: '' });
  }, [modal.open]);

  const handleSubmit = async () => {
    setSaving(true);
    modal.setError(null);
    try {
      await api(`/api/templates/${templateId}/variants`, {
        method: 'POST',
        body: JSON.stringify(form),
      });
      modal.close();
      onSaved();
    } catch (err) {
      modal.setError(translateError(err.code, err.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={modal.open} title={t('template.addVariant')} onClose={modal.close}>
      <div class="form-group">
        <label class="form-label">{t('template.variantName')} *</label>
        <input
          class="form-input"
          type="text"
          value={form.name}
          onInput={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
        />
      </div>
      <div class="form-group">
        <label class="form-label">{t('template.variantDescription')}</label>
        <textarea
          class="form-textarea"
          rows={2}
          value={form.description}
          onInput={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
        />
      </div>
      {modal.error && <div class="alert alert-error mb-3">{modal.error}</div>}
      <div class="flex gap-2 justify-end mt-4">
        <Button variant="secondary" onClick={modal.close}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!form.name || saving}>
          {saving ? t('common.loading') : t('common.save')}
        </Button>
      </div>
    </Modal>
  );
}

// ── main view ─────────────────────────────────────────────────────────────────

export default function TemplatesView() {
  const { hasPermission } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [roles, setRoles] = useState([]);
  const [members, setMembers] = useState([]);

  const [stepToEdit, setStepToEdit] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const newTemplateModal = useModal();
  const stepModal = useModal();
  const variantModal = useModal();
  const confirm = useConfirm();

  const [sectionGroups, setSectionGroups] = useState([]);
  const [newForm, setNewForm] = useState({ name: '', description: '', section: '', category: '' });
  const [newSaving, setNewSaving] = useState(false);
  // ✨ Steps proposed from the description; added only when kept and the template is created
  const [aiSteps, setAiSteps] = useState(null);

  const canCreate = hasPermission('templates', 'create');
  const canUpdate = hasPermission('templates', 'update');
  const canDelete = hasPermission('templates', 'delete');

  const loadDetail = (id) => {
    setDetailOpen(true);
    setLoadingDetail(true);
    api(`/api/templates/${id}`)
      .then(setSelected)
      .catch(() => setSelected(null))
      .finally(() => setLoadingDetail(false));
  };

  const fetchTemplates = (keepSelected = false) => {
    setLoading(true);
    api('/api/templates')
      .then((data) => {
        setTemplates(data);
        if (keepSelected && selected) loadDetail(selected.id);
      })
      .catch((err) => setError(translateError(err.code, err.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTemplates();
    api('/api/users/roles/list').then(setRoles).catch(() => setRoles([]));
    api('/api/users/members').then(setMembers).catch(() => setMembers([]));
    api('/api/settings/sections').then(setSectionGroups).catch(() => setSectionGroups([]));
  }, []);

  const handleCreateTemplate = async () => {
    setNewSaving(true);
    newTemplateModal.setError(null);
    try {
      const created = await api('/api/templates', {
        method: 'POST',
        body: JSON.stringify({ ...newForm }),
      });
      let stepsFailed = false;
      for (const [i, step] of (aiSteps || []).entries()) {
        try {
          await api(`/api/templates/${created.id}/steps`, { method: 'POST', body: JSON.stringify({
            title: step.title, description: step.description, step_order: i + 1,
            default_tasks_json: step.tasks.length ? JSON.stringify(step.tasks) : null,
          }) });
        } catch { stepsFailed = true; }
      }
      if (stepsFailed) alert('Het sjabloon is aangemaakt, maar niet alle voorgestelde stappen zijn toegevoegd. Vul ze aan in het sjabloon.');
      newTemplateModal.close();
      setAiSteps(null);
      setNewForm({ name: '', description: '', section: '', category: '' });
      await fetchTemplates();
      loadDetail(created.id);
    } catch (err) {
      newTemplateModal.setError(translateError(err.code, err.detail));
    } finally {
      setNewSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!selected) return;
    try {
      const updated = await api(`/api/templates/${selected.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !selected.is_active }),
      });
      setSelected(updated);
      setTemplates((prev) => prev.map((tpl) => (tpl.id === updated.id ? updated : tpl)));
    } catch (err) {
      setError(translateError(err.code, err.detail));
    }
  };

  const handleDeleteTemplate = () => {
    confirm.ask(
      t('template.delete'),
      t('template.confirmDelete'),
      async () => {
        setDetailError(null);
        try { await api(`/api/templates/${selected.id}`, { method: 'DELETE' }); }
        catch (err) { setDetailError(translateError(err.code, err.detail)); return; }
        setDetailOpen(false);
        setSelected(null);
        setTemplates((prev) => prev.filter((tpl) => tpl.id !== selected.id));
      }
    );
  };

  const openAddStep = () => {
    setStepToEdit(null);
    stepModal.setOpen(true);
  };

  const openEditStep = (step) => {
    setStepToEdit(step);
    stepModal.setOpen(true);
  };

  const handleRemoveStep = (step) => {
    confirm.ask(
      t('template.removeStep'),
      `"${step.title}" ${t('template.confirmRemoveStep')}`,
      async () => {
        setDetailError(null);
        try { await api(`/api/templates/${selected.id}/steps/${step.id}`, { method: 'DELETE' }); }
        catch (err) { setDetailError(translateError(err.code, err.detail)); return; }
        fetchTemplates(true);
      }
    );
  };

  const handleRemoveVariant = (variant) => {
    confirm.ask(
      t('template.removeVariant'),
      `"${variant.name}" ${t('template.confirmRemoveVariant')}`,
      async () => {
        setDetailError(null);
        try { await api(`/api/templates/${selected.id}/variants/${variant.id}`, { method: 'DELETE' }); }
        catch (err) { setDetailError(translateError(err.code, err.detail)); return; }
        fetchTemplates(true);
      }
    );
  };

  return (
    <div>
      <div class="page-header">
        <h1 class="page-title">{t('template.title')}</h1>
        {canCreate && (
          <Button variant="primary" onClick={() => newTemplateModal.setOpen(true)}>
            {t('template.newTemplate')}
          </Button>
        )}
      </div>

      {error && <div class="alert alert-error mb-4">{error}</div>}

      {/* Full-width template list */}
      {loading ? (
        <div class="card mt-4" style="overflow:hidden">
          <div style="display:grid;grid-template-columns:1fr 160px 80px 90px;gap:12px;padding:10px 16px;background:var(--color-surface-alt,#f9fafb);border-bottom:1px solid var(--color-border)">
            {['40%','20%','10%','10%'].map((w,i) => <div key={i} class="skeleton" style={`height:12px;width:${w};border-radius:4px`} />)}
          </div>
          <TemplatesSkeleton />
        </div>
      ) : templates.length === 0 ? (
        <div class="text-center text-muted" style="padding:60px">{t('common.noResults')}</div>
      ) : (
        <div class="card mt-4" style="overflow:hidden">
          {/* Header row */}
          <div style="display:grid;grid-template-columns:1fr 160px 80px 90px;gap:12px;padding:10px 16px;background:var(--color-surface-alt,#f9fafb);border-bottom:1px solid var(--color-border);font-size:12px;font-weight:600;color:var(--color-text-muted);text-transform:uppercase;letter-spacing:.04em">
            <span>{t('template.templateName')}</span>
            <span>{t('template.category')}</span>
            <span>{t('template.steps')}</span>
            <span>{t('common.status')}</span>
          </div>
          {templates.map((tpl) => (
            <div
              key={tpl.id}
              class="list-item-row"
              style="display:grid;grid-template-columns:1fr 160px 80px 90px;gap:12px;padding:12px 16px;border-bottom:1px solid var(--color-border);align-items:center;cursor:pointer;transition:background .15s"
              onClick={() => loadDetail(tpl.id)}
            >
              <div style="min-width:0">
                <div style="font-size:14px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{tpl.name}</div>
                {tpl.section && (
                  <div class="text-muted" style="font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{tpl.section}</div>
                )}
              </div>
              <div style="font-size:13px;color:var(--color-text-muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
                {tpl.category || '—'}
              </div>
              <div style="font-size:13px;color:var(--color-text-muted)">{tpl.stepCount}</div>
              <StatusBadge status={tpl.is_active ? 'actief' : 'inactief'} />
            </div>
          ))}
        </div>
      )}

      {/* Template detail modal */}
      <Modal
        isOpen={detailOpen}
        title={selected?.name || ''}
        onClose={() => { setDetailOpen(false); setDetailError(null); }}
        style="max-width:760px"
      >
        {loadingDetail ? (
          <div class="text-center text-muted" style="padding:40px">{t('common.loading')}</div>
        ) : selected ? (
          <div>
            {detailError && <div class="alert alert-error mb-3">{detailError}</div>}
            {/* Header info */}
            <div class="flex-between mb-3">
              <div>
                {(selected.section || selected.category) && (
                  <span class="text-sm text-muted">{[selected.section, selected.category].filter(Boolean).join(' › ')}</span>
                )}
                {selected.description && (
                  <p class="text-sm" style="color:var(--color-text-muted);margin-top:6px">{selected.description}</p>
                )}
              </div>
              <StatusBadge status={selected.is_active ? 'actief' : 'inactief'} />
            </div>

            <div class="flex gap-2 mb-4">
              {canUpdate && (
                <Button
                  variant={selected.is_active ? 'secondary' : 'primary'}
                  size="sm"
                  onClick={handleToggleActive}
                >
                  {selected.is_active ? t('template.deactivate') : t('template.activate')}
                </Button>
              )}
              {canDelete && (
                <Button variant="danger" size="sm" onClick={handleDeleteTemplate}>
                  {t('common.delete')}
                </Button>
              )}
            </div>

            {/* Steps */}
            <div style="border-top:1px solid var(--color-border);padding-top:16px;margin-bottom:16px">
              <div style="font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;color:var(--color-text-muted);margin-bottom:10px">{t('template.steps')}</div>
              <Card>

              {selected.steps?.length > 0 ? (
                  selected.steps.map((step) => {
                    let defaultTasks = [];
                    try { defaultTasks = step.default_tasks_json ? JSON.parse(step.default_tasks_json) : []; } catch {}
                    return (
                      <div key={step.id} class="list-item" style="align-items:flex-start">
                        <div style="flex:1">
                          <div style="display:flex;align-items:center;gap:8px;margin-bottom:2px;flex-wrap:wrap">
                            <span style="font-size:12px;font-weight:600;color:var(--color-text-muted);min-width:20px">
                              {step.step_order}.
                            </span>
                            <span style="font-size:14px;font-weight:500">{step.title}</span>
                            {step.is_approval_step ? (
                              <span style="font-size:11px;background:var(--color-info,#3b82f6);color:#fff;padding:1px 6px;border-radius:10px">
                                {t('template.isApprovalStep')}
                              </span>
                            ) : null}
                            {step.responsible_role_id && roles.find(r => r.id === step.responsible_role_id) && (
                              <span style="font-size:11px;background:var(--color-primary);color:#fff;padding:1px 8px;border-radius:10px">
                                {roles.find(r => r.id === step.responsible_role_id)?.name}
                              </span>
                            )}
                          </div>
                          {step.description && (
                            <div class="text-sm text-muted" style="margin-left:28px">{step.description}</div>
                          )}
                          {defaultTasks.length > 0 && (
                            <div style="margin-left:28px;margin-top:6px">
                              <div style="font-size:11px;font-weight:600;color:var(--color-text-muted);margin-bottom:4px;text-transform:uppercase;letter-spacing:.04em">
                                {t('template.defaultTasks')}
                              </div>
                              {defaultTasks.map((task, i) => {
                                const taskRole = task.responsible_role_id
                                  ? roles.find(r => String(r.id) === String(task.responsible_role_id))
                                  : null;
                                const pinnedUser = task.default_owner_id
                                  ? members.find(m => Number(m.id) === Number(task.default_owner_id))
                                  : null;
                                return (
                                  <div key={i} style="font-size:12px;color:var(--color-text-muted);padding:3px 0;display:flex;align-items:center;gap:6px;flex-wrap:wrap">
                                    <span style="color:var(--color-primary)">·</span>
                                    <span>
                                      <strong>{task.title}</strong>
                                      {task.description ? ` — ${task.description}` : ''}
                                    </span>
                                    {taskRole && (
                                      <span style="font-size:10px;background:var(--color-primary);color:#fff;padding:1px 6px;border-radius:10px;white-space:nowrap">
                                        {taskRole.name}
                                      </span>
                                    )}
                                    {pinnedUser && (
                                      <span style="font-size:10px;background:#f0fdf4;color:#16a34a;border:1px solid #bbf7d0;padding:1px 6px;border-radius:10px;white-space:nowrap">
                                        👤 {pinnedUser.display_name || pinnedUser.email}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        {canUpdate && (
                          <div class="flex gap-2" style="margin-left:8px;flex-shrink:0">
                            <Button variant="secondary" size="sm" onClick={() => openEditStep(step)}>
                              {t('common.edit')}
                            </Button>
                            <Button variant="danger" size="sm" onClick={() => handleRemoveStep(step)}>
                              {t('common.delete')}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p class="text-muted text-sm">{t('common.noResults')}</p>
                )}
                {canUpdate && (
                  <div class="mt-3">
                    <Button variant="secondary" size="sm" onClick={openAddStep}>
                      {t('template.addStep')}
                    </Button>
                  </div>
                )}
              </Card>
            </div>

            {/* Variants */}
            <div style="border-top:1px solid var(--color-border);padding-top:16px">
              <div style="font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;color:var(--color-text-muted);margin-bottom:10px">{t('template.variants')}</div>
              <Card>
                {selected.variants?.length > 0 ? (
                  selected.variants.map((variant) => (
                    <div key={variant.id} class="list-item">
                      <div style="flex:1">
                        <div style="font-size:14px;font-weight:500">{variant.name}</div>
                        {variant.description && (
                          <div class="text-sm text-muted">{variant.description}</div>
                        )}
                      </div>
                      {canUpdate && (
                        <Button variant="danger" size="sm" onClick={() => handleRemoveVariant(variant)}>
                          {t('common.delete')}
                        </Button>
                      )}
                    </div>
                  ))
                ) : (
                  <p class="text-muted text-sm">{t('common.noResults')}</p>
                )}
                {canUpdate && (
                  <div class="mt-3">
                    <Button variant="secondary" size="sm" onClick={() => variantModal.setOpen(true)}>
                      {t('template.addVariant')}
                    </Button>
                  </div>
                )}
              </Card>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* New template modal */}
      <Modal
        isOpen={newTemplateModal.open}
        title={t('template.newTemplate')}
        onClose={() => { setAiSteps(null); newTemplateModal.close(); }}
      >
        <div class="form-group">
          <label class="form-label">{t('template.templateName')} *</label>
          <input
            class="form-input"
            type="text"
            value={newForm.name}
            onInput={(e) => setNewForm((p) => ({ ...p, name: e.target.value }))}
          />
        </div>
        <div class="form-group">
          <label class="form-label">{t('template.description')}</label>
          <textarea
            class="form-textarea"
            rows={3}
            value={newForm.description}
            onInput={(e) => setNewForm((p) => ({ ...p, description: e.target.value }))}
          />
          <AiAssist kind="template_description" value={newForm.description} context={{ title: newForm.name }} onUse={text => setNewForm(p => ({ ...p, description: text }))} />
          {/* Adding steps needs templates:update (POST /api/templates/:id/steps) */}
          {canUpdate && <StepsFromDescription description={newForm.description} name={newForm.name} steps={aiSteps} onSteps={setAiSteps} />}
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
          <div class="form-group">
            <label class="form-label">Sectie</label>
            <select
              class="form-select"
              value={newForm.section}
              onChange={(e) => setNewForm((p) => ({ ...p, section: e.target.value, category: '' }))}
            >
              <option value="">— Kies sectie</option>
              {sectionGroups.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">{t('template.category')}</label>
            <select
              class="form-select"
              value={newForm.category}
              onChange={(e) => setNewForm((p) => ({ ...p, category: e.target.value }))}
              disabled={!newForm.section}
            >
              <option value="">— Kies categorie</option>
              {(sectionGroups.find(s => s.name === newForm.section)?.categories || []).map(c => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
        {newTemplateModal.error && (
          <div class="alert alert-error mb-3">{newTemplateModal.error}</div>
        )}
        <div class="flex gap-2 justify-end mt-4">
          <Button variant="secondary" onClick={() => { setAiSteps(null); newTemplateModal.close(); }}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            onClick={handleCreateTemplate}
            disabled={!newForm.name || newSaving}
          >
            {newSaving ? t('common.loading') : t('common.create')}
          </Button>
        </div>
      </Modal>

      {/* Step modal (add + edit) */}
      {selected && (
        <StepModal
          templateId={selected.id}
          stepToEdit={stepToEdit}
          onSaved={() => fetchTemplates(true)}
          modal={stepModal}
          roles={roles}
          members={members}
        />
      )}

      {/* Variant modal */}
      {selected && (
        <VariantModal
          templateId={selected.id}
          onSaved={() => fetchTemplates(true)}
          modal={variantModal}
        />
      )}

      {/* Generic confirm modal */}
      <Modal
        isOpen={confirm.state.open}
        title={confirm.state.title}
        onClose={confirm.close}
      >
        <p style="margin-bottom:24px;color:var(--color-text-muted);line-height:1.6">
          {confirm.state.message}
        </p>
        <div class="flex gap-2 justify-end">
          <Button variant="secondary" onClick={confirm.close} disabled={confirm.state.loading}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={confirm.confirm} disabled={confirm.state.loading}>
            {confirm.state.loading ? t('common.loading') : t('common.delete')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
